import InputDateFields from '@components/InputDateFields'
import cn from 'classnames'
import {
  extractCheckoutMethodFieldOptionLabel,
  isCheckoutMethodFieldReadOnly,
  prepareCheckoutMethodFieldOptions,
  selectCurrencyOptions,
  useAppSelector,
  useBanksRequest
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, SupportedBank, UseAdditionalFieldsOutput } from 'mmfintech-checkout-commons/types'
import {
  fixDateOnly,
  formatCardNumber,
  formatCardValidityPartial,
  getCountryAlpha2,
  isValidArray,
  parseCardValidity,
  tr
} from 'mmfintech-commons'
import { Input } from 'mmfintech-portal-commons'
import { Fragment } from 'react'
import styles from './Checkout.module.scss'

interface AdditionalFieldsStepProps {
  countryCode: string
  paymentMethod: string
  fieldValues: UseAdditionalFieldsOutput
}

export const AdditionalFieldsStep = (props: AdditionalFieldsStepProps) => {
  const { countryCode, paymentMethod, fieldValues } = props

  const isInterac = () => paymentMethod === 'INTERAC'

  const currencies = useAppSelector(selectCurrencyOptions)
  const { banks } = useBanksRequest()

  return (
    <>
      {isValidArray(fieldValues.fields)
        ? fieldValues.fields.map((field: CheckoutMethodField) => {
            // const { name, type, defaultLabel, translationKey, required, sensitive, options } = item

            if (
              fieldValues.isFieldHidden(field) ||
              field.name === 'bankChoiceId' ||
              field.name === 'expiryYear' ||
              field.name === 'cvv'
            ) {
              return null
            }

            if (field.name === 'cardNumber') {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  <Input
                    name={field.name}
                    type='text'
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    placeholder='0000 0000 0000 0000'
                    value={formatCardNumber(fieldValues.getValue('cardNumber'))}
                    onChange={(_: string, value: string) =>
                      fieldValues.setValue('cardNumber', value.replace(/\D+/g, ''))
                    }
                    error={fieldValues.getError(field.name)}
                    maxLength={19}
                    required
                    autoComplete='off'
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            if (field.name === 'expiryMonth') {
              return (
                <div className={cn(styles['checkout-input-wrapper'], styles['checkout-input-double'])} key={field.name}>
                  <Input
                    type='text'
                    name='expiryDate'
                    data-test='expiryDate-input'
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE', 'Expiry date')}
                    placeholder={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE_PLACEHOLDER', 'MM/YY')}
                    value={formatCardValidityPartial([
                      fieldValues.getValue('expiryMonth'),
                      fieldValues.getValue('expiryYear')
                    ])}
                    onChange={(_: string, value: string) => {
                      const [month, year] = parseCardValidity(value)
                      fieldValues.setValue('expiryMonth', month)
                      fieldValues.setValue('expiryYear', year)
                    }}
                    error={fieldValues.getError('expiryMonth') || fieldValues.getError('expiryYear')}
                    maxLength={5}
                    required
                    hideErrorLine
                    hideShowPassword
                    autoComplete='off'
                  />
                  <Input
                    type='password'
                    data-test='cvv-input'
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.CVV', 'CVV')}
                    {...fieldValues.registerInput('cvv')}
                    maxLength={4}
                    hideErrorLine
                    hideShowPassword
                    autoComplete='off'
                  />
                </div>
              )
            }

            if (field.name === 'swiftCode' && isInterac()) {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  <Input
                    type='select'
                    data-test='interac-bank-select'
                    options={banks?.map((bank: SupportedBank) => {
                      const { bankId, name, nameEnglish } = bank
                      return { value: bankId, label: nameEnglish || name }
                    })}
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.BANK', 'Bank')}
                    {...fieldValues.registerInput(field.name)}
                    hideErrorLine
                    menuPlacement='top'
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            if (field.name === 'phone') {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  <Input
                    type='phone'
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    disabled={isCheckoutMethodFieldReadOnly(field)}
                    autoComplete='off'
                    hideErrorLine
                    {...(isInterac()
                      ? {
                          country: 'ca',
                          onlyCountries: ['ca'],
                          countryCodeEditable: false,
                          disableDropdown: true
                        }
                      : {
                          country: getCountryAlpha2(countryCode)?.toLowerCase()
                        })}
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            if (field.name === 'documentId' && paymentMethod === 'PIX') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type={field.type || 'text'}
                    label={tr(
                      'MERCHANTS.CHECKOUT.FIELDS.GENERAL.CPF_CNPJ',
                      field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                    )}
                    data-test='pix-document-id-input'
                    {...fieldValues.registerShort(field.name)}
                    onChange={(name: string, value: string) => fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))}
                    required={fieldValues.isFieldRequired(field)}
                    autoComplete='off'
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.name === 'currency') {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  <Input
                    type='select'
                    data-test='currency-input'
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    options={currencies?.map((currency: string) => ({ value: currency, label: currency }))}
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            if (field.type.toLowerCase() === 'date') {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  <InputDateFields
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    value={fieldValues.getValue(field.name) ? new Date(fieldValues.getValue(field.name)) : null}
                    setValue={value => fieldValues.setValue(field.name, fixDateOnly(value))}
                    required={fieldValues.isFieldRequired(field)}
                    error={fieldValues.getError(field.name)}
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            if (field.type.toLowerCase() === 'select') {
              return (
                <div className={styles['checkout-input-wrapper']} key={field.name}>
                  {isCheckoutMethodFieldReadOnly(field) ? (
                    <Input
                      type='text'
                      data-test={`${field.name}-input`}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                      readOnly={true}
                      hideErrorLine
                    />
                  ) : (
                    <Input
                      type='select'
                      data-test={`${field.name}-select`}
                      options={prepareCheckoutMethodFieldOptions(field)}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      {...fieldValues.registerInput(field.name)}
                      menuPlacement='top'
                      hideErrorLine
                    />
                  )}
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <div className={styles['checkout-input-wrapper']} key={field.name}>
                <Input
                  type={field.sensitive ? 'password' : 'text'}
                  data-test={`${field.name}-input`}
                  label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                  {...fieldValues.registerInput(field.name)}
                  readOnly={isCheckoutMethodFieldReadOnly(field)}
                  autoComplete='off'
                  hideErrorLine
                />
                {Boolean(field.description) && (
                  <div className='input-description'>
                    {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                  </div>
                )}
              </div>
            )
          })
        : null}
    </>
  )
}
